import classNames from 'classnames'
import React, { MouseEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { Transition } from 'react-transition-group'

import { Button } from '../button/button.js'

export interface NotificationProps {
    children: ReactNode
    className?: string
    duration?: number
    isClosable?: boolean
    isDefaultOpen?: boolean
    isOpen?: boolean
    onChange?: (isOpen: boolean) => void
    type?: 'error' | 'info'
}

export const Notification = (props: NotificationProps): JSX.Element => {
    const timeout = useRef<number | undefined>(undefined)
    const [isOpen, setIsOpen] = useState<boolean>(props.isDefaultOpen || false)

    const { children, duration, isClosable = true, onChange, type = 'error' } = props

    const hideNotification = useCallback((): void => {
        setIsOpen(false)

        if (onChange) {
            onChange(false)
        }
    }, [setIsOpen, onChange])

    const getDuration: () => number | undefined = useCallback(() => {
        const defaultInfoDuration = 4000
        const defaultErrorDuration = 6000

        if (duration) {
            return duration
        } else if (!isClosable) {
            if (type === 'info') {
                return defaultInfoDuration
            } else {
                return defaultErrorDuration
            }
        } else {
            return undefined
        }
    }, [duration, isClosable, type])

    const hideWithDelay = useCallback((): void => {
        timeout.current = window.setTimeout(() => {
            hideNotification()
        }, getDuration())
    }, [getDuration, hideNotification])

    useEffect(() => {
        if (timeout) {
            window.clearTimeout(timeout.current)
        }

        if (getDuration()) {
            hideWithDelay()
        }
    }, [getDuration, hideWithDelay, timeout])

    const getIsShown = (): boolean => {
        return props.onChange && typeof props.isOpen !== 'undefined' ? props.isOpen : isOpen
    }

    const className = (state: string): string =>
        classNames(
            'notification',
            type && `notification--type-${type}`,
            isClosable && 'is-closable',
            `is-${state}`,
            props.className,
        )

    const handleCloseClick = (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>): void => {
        event.preventDefault()

        hideNotification()
    }

    const onEntered = (): void => {
        if (timeout) {
            window.clearTimeout(timeout.current)
        }

        if (duration) {
            hideWithDelay()
        }
    }

    const onExited = (): void => {
        if (timeout) {
            window.clearTimeout(timeout.current)
        }
    }

    const notification = (state: string): JSX.Element => (
        <div className={className(state)}>
            <div className="notification__inner">
                <div className="notification__content">{children}</div>
                {isClosable && (
                    <div className="notification__close-wrapper">
                        <Button
                            className="notification__close"
                            onClick={handleCloseClick}
                            text="Close"
                            style="underlined"
                        />
                    </div>
                )}
            </div>
        </div>
    )

    return (
        <Transition
            in={getIsShown()}
            timeout={{ enter: 0, exit: 300 }}
            duration={300}
            mountOnEnter={true}
            unmountOnExit={true}
            onEntered={onEntered}
            onExited={onExited}
        >
            {notification}
        </Transition>
    )
}
